import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Lock, Plus, X, Eye, EyeSlash, Copy, Pencil, Trash, ArrowsClockwise,
  MagnifyingGlass, ShieldCheck, UserCircle, Globe,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICON = {
  Bank: '🏦', Gmail: '📧', Envato: '🎨', Canva: '🖼️', ChatGPT: '🤖',
  'Putty / SSH': '💻', FTP: '📁', Hosting: '☁️', Domain: '🌐',
  WordPress: '📝', 'Social Media': '📱', 'Payment Gateway': '💳', Other: '🔑',
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) toast.success('Copied to clipboard');
    else toast.error('Copy blocked — your browser denied clipboard access');
    return ok;
  } catch (err) {
    toast.error('Copy blocked — your browser denied clipboard access');
    return false;
  }
};

const passwordStrength = (p = '') => {
  if (!p) return { label: '—', color: '#94a3b8', score: 0 };
  let score = 0;
  if (p.length >= 8) score++;
  if (p.length >= 12) score++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
  if (/[0-9]/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  if (score <= 1) return { label: 'Weak', color: '#dc2626', score };
  if (score <= 3) return { label: 'OK', color: '#d97706', score };
  return { label: 'Strong', color: '#15803d', score };
};

const generatePassword = (len = 18) => {
  const charset = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%^&*()_-+=';
  const out = [];
  const buf = new Uint32Array(len);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  for (let i = 0; i < len; i++) out.push(charset[buf[i] % charset.length]);
  return out.join('');
};

export default function PersonalVaultPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'Other', username: '', url: '', notes: '', password: '' });
  const [showPwInForm, setShowPwInForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({});
  const autoHideRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCat !== 'all') params.category = filterCat;
      if (query) params.q = query;
      const [r, c] = await Promise.all([
        axios.get(`${API_URL}/api/personal-vault`, { params, withCredentials: true }).catch((e) => {
          const msg = e.response?.data?.detail || e.message;
          if (e.response?.status === 503) toast.error(msg, { duration: 12000 });
          else if (e.response?.status === 403) toast.error(msg, { duration: 8000 });
          else toast.error('Failed to load personal vault: ' + msg);
          return { data: [] };
        }),
        axios.get(`${API_URL}/api/personal-vault/categories`, { withCredentials: true })
          .catch(() => ({ data: ['Bank', 'Gmail', 'Envato', 'Canva', 'ChatGPT', 'Putty / SSH', 'FTP', 'Hosting', 'Domain', 'WordPress', 'Social Media', 'Payment Gateway', 'Other'] })),
      ]);
      setList(r.data || []);
      setCategories(c.data || []);
    } catch (e) {
      toast.error('Failed to load: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [filterCat, query]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const map = {};
    for (const e of list) map[e.category] = (map[e.category] || 0) + 1;
    return map;
  }, [list]);

  const startNew = () => {
    setEditing({ id: null });
    setForm({ title: '', category: 'Other', username: '', url: '', notes: '', password: '' });
    setShowPwInForm(false);
    setOpen(true);
  };

  const startEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title || '', category: e.category || 'Other',
      username: e.username || '', url: e.url || '', notes: e.notes || '',
      password: '',
    });
    setShowPwInForm(false);
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!editing?.id && !form.password) return toast.error('Password is required');
    setSaving(true);
    try {
      if (editing?.id) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await axios.put(`${API_URL}/api/personal-vault/${editing.id}`, payload, { withCredentials: true });
        toast.success('Personal entry updated');
      } else {
        await axios.post(`${API_URL}/api/personal-vault`, form, { withCredentials: true });
        toast.success('Saved to your personal vault');
      }
      setOpen(false); setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setSaving(false);
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"? The password cannot be recovered after this.`)) return;
    try {
      await axios.delete(`${API_URL}/api/personal-vault/${e.id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const reveal = async (e) => {
    if (revealed[e.id]) {
      setRevealed((s) => { const n = { ...s }; delete n[e.id]; return n; });
      if (autoHideRef.current[e.id]) { clearTimeout(autoHideRef.current[e.id]); delete autoHideRef.current[e.id]; }
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/personal-vault/${e.id}/reveal`, {}, { withCredentials: true });
      if (r.data?._decrypt_error) toast.error(r.data._decrypt_error);
      setRevealed((s) => ({ ...s, [e.id]: r.data.password || '' }));
      autoHideRef.current[e.id] = setTimeout(() => {
        setRevealed((s) => { const n = { ...s }; delete n[e.id]; return n; });
      }, 30000);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const copyPassword = async (e) => {
    try {
      let pw = revealed[e.id];
      if (!pw) {
        const r = await axios.post(`${API_URL}/api/personal-vault/${e.id}/reveal`, {}, { withCredentials: true });
        pw = r.data?.password || '';
      }
      if (!pw) return toast.error('Could not retrieve password');
      await copyToClipboard(pw);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape' && open) setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="space-y-4" data-testid="personal-vault-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <UserCircle size={22} weight="duotone" /> Personal Vault
          </h1>
          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
            <ShieldCheck size={12} weight="fill" className="text-emerald-600" />
            Strictly private — only you can see these entries. AES-256 encrypted at rest. No sharing, no audit log.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={startNew} data-testid="pvault-new-btn">
            <Plus size={14} className="mr-1" /> New Entry
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px]">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="title, username, url, notes…"
              className="pl-8"
              data-testid="pvault-search"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px]">Category</Label>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="border rounded px-2 py-1.5 text-xs bg-white"
            data-testid="pvault-cat-filter"
          >
            <option value="all">All ({list.length})</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_ICON[c] || '🔑'} {c} {counts[c] ? `(${counts[c]})` : ''}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pvault-refresh">
          <ArrowsClockwise size={12} className="mr-1" /> Refresh
        </Button>
      </Card>

      {/* List */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-gray-500">Loading…</Card>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center text-sm text-gray-500" data-testid="pvault-empty">
          <Lock size={28} className="mx-auto mb-2 text-gray-300" />
          {query || filterCat !== 'all'
            ? 'No entries match your filters.'
            : 'Your personal vault is empty. Click “New Entry” to save your first credential.'}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pvault-list">
          {list.map((e) => {
            const isRevealed = !!revealed[e.id];
            return (
              <Card key={e.id} className="p-3 border-l-4" style={{ borderLeftColor: '#7c3aed' }} data-testid={`pvault-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-2xl flex-shrink-0">{CATEGORY_ICON[e.category] || '🔑'}</div>
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate" style={{ color: '#1a2e4a' }} data-testid={`pvault-title-${e.id}`}>{e.title}</div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{e.category}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(e)} className="p-1 text-gray-400 hover:text-gray-700" data-testid={`pvault-edit-${e.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(e)} className="p-1 text-gray-400 hover:text-rose-600" data-testid={`pvault-del-${e.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </div>

                {e.username && (
                  <div className="mt-2 flex items-center gap-1 text-xs">
                    <span className="text-gray-500 w-16">Username</span>
                    <span className="font-mono flex-1 truncate text-gray-800">{e.username}</span>
                    <button onClick={() => copyToClipboard(e.username)} className="p-1 text-gray-400 hover:text-emerald-600" title="Copy username"><Copy size={12} /></button>
                  </div>
                )}

                <div className="mt-1 flex items-center gap-1 text-xs">
                  <span className="text-gray-500 w-16">Password</span>
                  <span className="font-mono flex-1 truncate text-gray-800" data-testid={`pvault-password-${e.id}`}>
                    {isRevealed ? revealed[e.id] : '••••••••••••'}
                  </span>
                  <button
                    onClick={() => reveal(e)}
                    className={`p-1 ${isRevealed ? 'text-emerald-600' : 'text-gray-400 hover:text-emerald-600'}`}
                    title={isRevealed ? 'Hide' : 'Reveal'}
                    data-testid={`pvault-reveal-${e.id}`}
                  >
                    {isRevealed ? <EyeSlash size={12} /> : <Eye size={12} />}
                  </button>
                  <button onClick={() => copyPassword(e)} className="p-1 text-gray-400 hover:text-emerald-600" title="Copy password" data-testid={`pvault-copy-${e.id}`}><Copy size={12} /></button>
                </div>

                {e.url && (
                  <div className="mt-1 flex items-center gap-1 text-xs">
                    <span className="text-gray-500 w-16">URL</span>
                    <a href={e.url} target="_blank" rel="noreferrer" className="text-blue-600 underline truncate flex-1 inline-flex items-center gap-1">
                      <Globe size={11} /> {e.url}
                    </a>
                  </div>
                )}

                {e.notes && (
                  <div className="mt-2 text-[11px] text-gray-600 bg-gray-50 rounded px-2 py-1 whitespace-pre-wrap">{e.notes}</div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal: New / Edit */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <Card className="w-full max-w-lg bg-white p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pvault-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
                <Lock size={16} /> {editing?.id ? 'Edit Personal Entry' : 'New Personal Entry'}
              </h3>
              <button onClick={() => setOpen(false)} className="text-gray-400"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. ICICI personal account" data-testid="pvault-title-input" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Category</Label>
                  <select
                    className="w-full border rounded px-2 py-2 text-sm bg-white"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="pvault-category-input"
                  >
                    {categories.map((c) => <option key={c} value={c}>{CATEGORY_ICON[c] || '🔑'} {c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">URL</Label>
                  <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="pvault-url-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Username / Identifier</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="email / account number / username" data-testid="pvault-username-input" />
              </div>
              <div>
                <Label className="text-xs">
                  Password {editing?.id ? <span className="text-gray-500 font-normal">(leave blank to keep current)</span> : '*'}
                </Label>
                <div className="flex gap-1">
                  <div className="relative flex-1">
                    <Input
                      type={showPwInForm ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder={editing?.id ? 'Leave blank to keep current' : 'Enter password'}
                      className="pr-9 font-mono"
                      data-testid="pvault-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwInForm((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                      data-testid="pvault-password-toggle"
                    >
                      {showPwInForm ? <EyeSlash size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setForm({ ...form, password: generatePassword(18) }); setShowPwInForm(true); }}
                    data-testid="pvault-password-generate"
                    title="Generate a strong random password"
                  >
                    Generate
                  </Button>
                </div>
                {form.password && (() => {
                  const s = passwordStrength(form.password);
                  return (
                    <div className="flex items-center gap-2 mt-1 text-[10px]">
                      <div className="flex-1 h-1 bg-slate-200 rounded overflow-hidden">
                        <div style={{ width: `${(s.score / 5) * 100}%`, background: s.color, height: '100%' }} />
                      </div>
                      <span style={{ color: s.color }} className="font-bold uppercase">{s.label}</span>
                    </div>
                  );
                })()}
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <textarea
                  className="w-full border rounded px-2 py-2 text-sm min-h-[60px]"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Recovery email, security questions, 2FA notes…"
                  data-testid="pvault-notes-input"
                />
              </div>
              <div className="text-[10px] text-gray-500 bg-purple-50 border border-purple-100 rounded px-2 py-1.5 flex items-start gap-2">
                <UserCircle size={14} className="text-purple-600 mt-0.5 flex-shrink-0" />
                <span>This entry is private to <strong>{user?.name || user?.email}</strong> and cannot be shared. Other admins on this workspace will not see it.</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="pvault-save-btn">
                {saving ? 'Saving…' : (editing?.id ? 'Update' : 'Save')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
